import { api, ApiError } from './api'

export type AdminRole = 'superadmin' | 'admin' | 'hr' | 'viewer'

export type AdminSession = {
  kind: 'admin'
  id: number
  company: string
  username: string
  displayName: string
  email: string | null
  empCode: string | null
  role: AdminRole
  mustChangePassword: boolean
  lastLoginAt: string | null
  /** รหัสพนักงานที่กำลัง "เข้าแทน" อยู่ — ไม่มีคีย์นี้ = ใช้งานเป็นตัวเองตามปกติ */
  actingAs?: string | null
}

export type UserSession = {
  kind: 'user'
  id: number
  company: string
  username: string
  displayName: string
  empCode: string | null
  department: string | null
  position: string | null
}

export type Session = AdminSession | UserSession

export type SessionKind = Session['kind']

/** ทุก endpoint ของ auth ห่อ session ไว้ในคีย์ `session` (ทั้ง login และ me) */
type SessionResponse = { session: Session }

/**
 * ล็อกอิน — path แยกตาม kind เพราะ backend คนละตารางคนละ policy
 *
 * ตั้งใจใช้ `api()` ตรง ๆ ไม่ใช่ตัว wrap ใน SessionContext:
 * 401 ที่นี่คือ "รหัสผ่านไม่ถูกต้อง" ไม่ใช่ session หมดอายุ ถ้าเผลอใช้ตัว wrap
 * ผู้ใช้จะเห็นข้อความ "เซสชันหมดอายุ" ทั้งที่แค่พิมพ์รหัสผิด
 */
export function login(kind: SessionKind, username: string, password: string): Promise<Session> {
  return api<SessionResponse>(`/api/auth/${kind}/login`, {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  }).then((res) => res.session)
}

/**
 * 401 = ยังไม่ได้ล็อกอิน ซึ่งเป็นเรื่องปกติ ไม่ใช่ error → คืน null
 * (ถ้าโยนต่อ คนที่เพิ่งเปิดเว็บจะเจอกล่องแดงทุกครั้ง)
 * error อื่น (backend ล่ม / CORS / ไม่ได้ตั้ง VITE_API_BASE) ยังโยนต่อตามเดิม
 */
export async function fetchMe(): Promise<Session | null> {
  try {
    const res = await api<SessionResponse>('/api/auth/me')
    return res.session
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null
    throw err
  }
}

export async function logout(): Promise<void> {
  await api<{ ok: true }>('/api/auth/logout', { method: 'POST' })
}

/**
 * รหัสผ่านใหม่ต้องยาวเท่านี้ — ตัวเลขเดียวกับที่ backend บังคับ
 * (backend/src/lib/adminAuth.js → MIN_PASSWORD ซึ่งเป็นแหล่งเดียวของฝั่งนั้น)
 * ตั้งต่ำกว่านั้นจะกลายเป็นบอกผู้ใช้ว่าผ่านแล้วแต่ backend ตีกลับ
 */
export const PASSWORD_MIN_LENGTH = 5

/**
 * เปลี่ยนรหัสผ่านของตัวเอง (admin เท่านั้น)
 *
 * backend แยก status ให้ชัดแล้ว: **400 = รหัสผ่านเดิมไม่ถูกต้อง** (หรือรหัสใหม่ไม่ผ่านเกณฑ์)
 * ส่วน 401 หมายถึง session หมดอายุจริง ๆ เท่านั้น → หน้าจอไม่ต้องเดาเอง
 * ดูเหตุผลที่ backend ไม่ใช้ 401 กับรหัสเดิมผิดที่ backend/src/lib/adminAuth.js
 *
 * backend ขยับ token_version แล้วออก cookie ใบใหม่ พร้อมส่ง session ที่อัปเดตแล้วกลับมาด้วย
 * (ใช้ค่านั้นได้เลย ไม่ต้องยิง /api/auth/me ซ้ำ)
 */
export function changePassword(
  current: string,
  next: string,
): Promise<{ ok: true; session?: Session }> {
  return api<{ ok: true; session?: Session }>('/api/auth/password', {
    method: 'POST',
    body: JSON.stringify({ current, next }),
  })
}

const ROLE_LABEL: Record<AdminRole, string> = {
  superadmin: 'ผู้ดูแลระบบสูงสุด',
  admin: 'ผู้ดูแล',
  hr: 'ฝ่ายบุคคล',
  viewer: 'ดูข้อมูลเท่านั้น',
}

export function roleLabel(role: AdminRole): string {
  return ROLE_LABEL[role]
}

/** ข้อความบอกว่ากำลังใช้สิทธิ์อะไรอยู่ — โชว์บน header กันสับสนเวลามีสองบัญชี */
export function sessionLabel(session: Session): string {
  return session.kind === 'admin' ? `ผู้ดูแล · ${roleLabel(session.role)}` : 'พนักงาน'
}
